import React from 'react';

// Importing icons to match the image
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import ArchiveIcon from '@material-ui/icons/Archive';

// --- Data Constants ---

const SUMMARY_METRICS = [
    { label: 'Demand Shift', value: '0.62M cases', delta: 'changed vs plan', tone: 'blue', icon: <TrendingUpIcon />, deltaTone: 'warn' },
    { label: 'Stock to Reallocate', value: '0.41M cases', delta: 'recommended', tone: 'purple', icon: <SwapHorizIcon />, deltaTone: 'warn' },
    { label: 'Lost Sales Avoided', value: '₹24 Cr', delta: 'estimated', tone: 'green', icon: <AttachMoneyIcon />, deltaTone: 'good' },
    { label: 'Inventory Released', value: '₹18 Cr', delta: 'from excess markets', tone: 'orange', icon: <ArchiveIcon />, deltaTone: 'good' },
];

const DEPLOYMENT_DATA = [
    { market: 'Maharashtra', original: '620k', latestDemand: '710k', available: '665k', recommended: '700k', delta: '+80k', risk: 'High', tone: 'good' },
    { market: 'Karnataka', original: '410k', latestDemand: '455k', available: '438k', recommended: '450k', delta: '+40k', risk: 'Medium', tone: 'good' },
    { market: 'Delhi NCR', original: '355k', latestDemand: '330k', available: '390k', recommended: '335k', delta: '-20k', risk: 'Low', tone: 'bad' },
    { market: 'West Bengal', original: '300k', latestDemand: '255k', available: '345k', recommended: '270k', delta: '-30k', risk: 'Low', tone: 'bad' },
    { market: 'Telangana', original: '285k', latestDemand: '335k', available: '298k', recommended: '330k', delta: '+45k', risk: 'High', tone: 'good' },
];

const REALLOCATION_LOGIC = [
    { rank: '1', title: 'Detect', detail: 'Demand changed vs plan', value: 'State × Brand × SKU', tone: 'blue' },
    { rank: '2', title: 'Quantify', detail: 'Lost sales / excess risk', value: 'Cases + ₹ value', tone: 'warn' },
    { rank: '3', title: 'Optimize', detail: 'Allocate constrained stock', value: 'Service + NSV + margin', tone: 'good' },
    { rank: '4', title: 'Route', detail: 'Choose source / lane', value: 'Cost + lead time', tone: 'blue' },
    { rank: '5', title: 'Execute', detail: 'Transfer / dispatch action', value: 'Owner + due date', tone: 'good' },
];

const STOCK_MOVES_DATA = [
    { from: 'West Bengal', to: 'Maharashtra', sku: 'Premium A', original: '0', recommended: '65k', move: '+65k', why: 'Demand +14%; WB excess 11 days', impact: '₹9 Cr lost sales avoided', tone: 'good' },
    { from: 'Delhi NCR', to: 'Karnataka', sku: 'Core B', original: '0', recommended: '38k', move: '+38k', why: 'Karnataka demand +11%', impact: 'Service +2.4 pp', tone: 'good' },
    { from: 'Central DC', to: 'Telangana', sku: 'Premium F', original: '20k', recommended: '55k', move: '+35k', why: 'Premium upside / low cover', impact: '₹5 Cr NSV protected', tone: 'good' },
    { from: 'East DC', to: 'West Bengal', sku: 'Core D', original: '85k', recommended: '45k', move: '-40k', why: 'Demand below plan; excess risk', impact: '₹4 Cr inventory released', tone: 'bad' },
    { from: 'North DC', to: 'Maharashtra', sku: 'Value E', original: '110k', recommended: '135k', move: '+25k', why: 'Fill-rate risk next 10 days', impact: 'Stock-out avoided', tone: 'good' },
];

export class DeploymentLogistics extends React.Component {
    render() {
        return (
            <div className="cc-page">
                {/* Header */}
                <div className="cc-header">
                    <div className="cc-header-text">
                        <h1>Deployment Optimizer — Plan vs Demand</h1>
                        <p>Continuously rebalance inventory to the markets where the latest demand creates the highest service and economic value.</p>
                    </div>
                    <div className="cc-header-filters">
                        <button className="cc-filter-btn">Next 8 Weeks</button>
                        <button className="cc-filter-btn">All Sites</button>
                        <button className="cc-filter-btn">All Brands</button>
                    </div>
                </div>

                {/* Top Metrics Row - Updated to match the image style */}
                <div className="cc-metric-row">
                    {SUMMARY_METRICS.map((metric) => (
                        <div className={`cc-metric-card tone-${metric.tone}`} key={metric.label}>
                            <div className="cc-metric-icon-wrap">{metric.icon}</div>
                            <div className="cc-metric-content">
                                <div className="cc-metric-label">{metric.label}</div>
                                <div className="cc-metric-value">{metric.value}</div>
                                <div className={`cc-metric-delta cc-delta-${metric.deltaTone}`}>
                                    {metric.delta}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Middle Row: Deployment Table & Logic */}
                <div className="cc-main-row">
                    {/* Left Panel: Market Deployment */}
                    <div className="cc-panel cc-panel-table ">
                        <h2>Market Deployment — Original Plan vs Latest Demand</h2>
                        <p className="cc-panel-sub">Green = deploy more • Red = reduce • Grey = retain original allocation</p>
                        
                        <table className="cc-actions-table cc-table-striped cc-table-light">
                            <thead>
                                <tr>
                                    <th>Market</th>
                                    <th>Original Plan</th>
                                    <th>Latest Demand</th>
                                    <th>Available Stock</th>
                                    <th>Recommended</th>
                                    <th>Δ</th>
                                    <th>Service Risk</th>
                                </tr>
                            </thead>
                            <tbody>
                                {DEPLOYMENT_DATA.map((row) => (
                                    <tr key={row.market}>
                                        <td>{row.market}</td>
                                        <td>{row.original}</td>
                                        <td>{row.latestDemand}</td>
                                        <td>{row.available}</td>
                                        <td>{row.recommended}</td>
                                        <td className={`cc-delta-${row.tone}`}>{row.delta}</td>
                                        <td className={`cc-delta-${row.risk === 'High' ? 'bad' : row.risk === 'Medium' ? 'warn' : 'good'}`}>
                                            {row.risk}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Right Panel: Reallocation Logic - Updated to match the image style */}
                    <div className="cc-panel cc-exceptions-panel">
                        <h2>Demand-Driven Reallocation Logic</h2>
                        <div className="cc-exceptions-list">
                            {REALLOCATION_LOGIC.map((item) => (
                                <div className="cc-exception-row" key={item.rank}>
                                    <span className="cc-exception-rank">{item.rank}</span>
                                    <div className="cc-exception-text-wrap">
                                        <span className="cc-exception-title">{item.title}</span>
                                        <span className="cc-exception-detail">{item.detail}</span>
                                    </div>
                                    <span className={`cc-exception-value cc-delta-${item.tone}`}>{item.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Bottom Row: Recommended Stock Moves */}
                <div className="cc-panel cc-panel-table">
                    <h2>Recommended Stock Moves & Deployment Changes</h2>
                    <p className="cc-panel-sub">Compare original deployment with optimizer actions triggered by latest demand and inventory position</p>

                    <table className="cc-actions-table cc-table-striped cc-table-light">
                        <thead>
                            <tr>
                                <th>From</th>
                                <th>To</th>
                                <th>Brand / SKU</th>
                                <th>Original</th>
                                <th>Recommended</th>
                                <th>Move</th>
                                <th>Why Now?</th>
                                <th>Value / Service Impact</th>
                            </tr>
                        </thead>
                        <tbody>
                            {STOCK_MOVES_DATA.map((row, index) => (
                                <tr key={index}>
                                    <td>{row.from}</td>
                                    <td>{row.to}</td>
                                    <td>{row.sku}</td>
                                    <td>{row.original}</td>
                                    <td>{row.recommended}</td>
                                    <td className={`cc-delta-${row.tone}`}>{row.move}</td>
                                    <td>{row.why}</td>
                                    <td className={`cc-delta-${row.tone}`}>{row.impact}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Footnote */}
                <p className="cc-footnote">
                    Illustrative InBrew UX and values for discussion; actual metrics and thresholds would be configured from InBrew data and operating definitions.
                </p>
            </div>
        );
    }
}

export default DeploymentLogistics;